import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, IconButton, LinearProgress, Stack, Typography, alpha } from '@mui/material';
import { RocketLaunchRounded, CloseRounded, ArrowForwardRounded } from '@mui/icons-material';
import { usePermissions } from '../../hooks/use-permissions';
import { useTenantStore } from '../../stores/tenant.store';
import { useSetupProgress } from '../../hooks/use-setup-progress';

const dismissKey = (tid: string) => `teach-setup-banner-dismissed-${tid}`;

/**
 * Dashboard banner nudging admins to finish setup. Shown to admins while
 * essentials are incomplete and the banner hasn't been dismissed for this tenant.
 */
export function SetupBanner() {
  const { t } = useTranslation('setup');
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const currentTenantId = useTenantStore((s) => s.currentTenantId);
  const { loading, pct } = useSetupProgress();

  const [dismissed, setDismissed] = useState(
    () => !!currentTenantId && localStorage.getItem(dismissKey(currentTenantId)) === '1',
  );

  if (!isAdmin || loading || pct >= 100 || dismissed) return null;

  function dismiss() {
    if (currentTenantId) localStorage.setItem(dismissKey(currentTenantId), '1');
    setDismissed(true);
  }

  return (
    <Box
      sx={{
        mb: 3,
        p: 2.5,
        borderRadius: 3,
        border: (th) => `1px solid ${alpha(th.palette.primary.main, 0.35)}`,
        bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <RocketLaunchRounded sx={{ color: 'primary.main', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('banner.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={1.25}>
            {t('banner.body', { pct })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ height: 6, borderRadius: 3, maxWidth: 360, mb: 1.5 }}
          />
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardRounded />}
            onClick={() => navigate('/empezar')}
          >
            {t('banner.cta')}
          </Button>
        </Box>
        <IconButton size="small" onClick={dismiss} aria-label={t('banner.dismiss')}>
          <CloseRounded fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}
